import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import MarkChatUnreadRoundedIcon from "@mui/icons-material/MarkChatUnreadRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { customersApi } from "../../api/customers";

const INK = "var(--wp-panel-text-primary, #19242F)";
const MUTED = "rgba(var(--wp-panel-ink-rgb, 25,36,47),.66)";

function dateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "short", timeStyle: "short" }).format(date);
}

function relativeWindow(value) {
  if (!value) return "Sin ventana abierta";
  const diff = new Date(value).getTime() - Date.now();
  if (!Number.isFinite(diff) || diff <= 0) return "Ventana cerrada";
  const hours = Math.floor(diff / 3600000);
  const minutes = Math.max(0, Math.floor((diff % 3600000) / 60000));
  return hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`;
}

function statusLabel(snapshot) {
  if (snapshot?.connected) return "Conectado";
  if (snapshot?.configured) return "Falta activar";
  return "Sin configurar";
}

function templateHasVariables(template) {
  return (template?.components || []).some((component) => {
    const text = String(component?.text || "");
    if (/\{\{\d+\}\}/.test(text)) return true;
    return Array.isArray(component?.buttons) && component.buttons.some((button) => /\{\{\d+\}\}/.test(String(button?.url || button?.text || "")));
  });
}

function ConversationRow({ row, selected, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        width: "100%",
        textAlign: "left",
        border: 0,
        borderBottom: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.06)",
        bgcolor: selected ? "rgba(37,211,102,.075)" : "transparent",
        cursor: "pointer",
        px: 1.1,
        py: .9,
        color: "inherit",
        "&:hover": { bgcolor: selected ? "rgba(37,211,102,.09)" : "rgba(var(--wp-panel-ink-rgb,25,36,47),.025)" },
      }}
    >
      <Stack direction="row" spacing={.75} alignItems="flex-start">
        <Box sx={{ width: 34, height: 34, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: "rgba(37,211,102,.12)", color: "#168A46", flex: "0 0 auto", fontWeight: 950 }}>
          {(row.contactName || "C").slice(0, 1).toUpperCase()}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={.5} justifyContent="space-between" alignItems="center">
            <Typography variant="body2" noWrap sx={{ fontWeight: row.unreadCount ? 1000 : 850, color: INK, maxWidth: 170 }}>{row.contactName || row.customer?.name || row.contactPhone || "Contacto"}</Typography>
            <Typography variant="caption" sx={{ color: MUTED, fontSize: 10.5, whiteSpace: "nowrap" }}>{dateTime(row.lastMessageAt)}</Typography>
          </Stack>
          <Stack direction="row" spacing={.45} alignItems="center" sx={{ mt: .15 }}>
            {row.ai?.handoffRequired ? <Chip size="small" label="Humano" color="warning" sx={{ height: 18, fontSize: 9.5, fontWeight: 900 }} /> : null}
            <Typography variant="caption" noWrap sx={{ color: MUTED, flex: 1 }}>{row.lastMessagePreview || "Sin mensajes todavía"}</Typography>
            {row.unreadCount ? <Chip size="small" label={row.unreadCount} sx={{ height: 19, minWidth: 25, fontSize: 10.5, fontWeight: 950, bgcolor: "#25D366", color: "#fff" }} /> : null}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}

function MessageBubble({ message }) {
  const outbound = message.direction === "outbound";
  return (
    <Box sx={{ display: "flex", justifyContent: outbound ? "flex-end" : "flex-start", px: .6 }}>
      <Box
        sx={{
          maxWidth: "78%",
          px: 1,
          py: .7,
          borderRadius: outbound ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
          bgcolor: outbound ? "rgba(37,211,102,.13)" : "var(--wp-panel-surface, #fff)",
          border: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.07)",
          boxShadow: "0 1px 4px rgba(25,36,47,.04)",
        }}
      >
        {message.type === "template" ? <Typography variant="caption" sx={{ display: "block", mb: .25, fontWeight: 900, color: "#168A46" }}>Plantilla · {message.template?.name || "WhatsApp"}</Typography> : null}
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", color: INK }}>{message.text || message.media?.caption || `[${message.type || "mensaje"}]`}</Typography>
        <Stack direction="row" spacing={.45} justifyContent="flex-end" alignItems="center" sx={{ mt: .3 }}>
          {message.ai?.generated ? <AutoAwesomeRoundedIcon sx={{ fontSize: 12, color: "#7B61FF" }} /> : null}
          <Typography variant="caption" sx={{ color: MUTED, fontSize: 10 }}>{dateTime(message.providerTimestamp || message.createdAt)}</Typography>
          {outbound ? <Typography variant="caption" sx={{ color: message.status === "failed" ? "error.main" : MUTED, fontSize: 10 }}>{message.status || ""}</Typography> : null}
        </Stack>
      </Box>
    </Box>
  );
}

export default function CrmOmnichannelHub({ projectId, canManage = false, onConfigure }) {
  const [overview, setOverview] = React.useState(null);
  const [expanded, setExpanded] = React.useState(false);
  const [conversations, setConversations] = React.useState([]);
  const [selectedId, setSelectedId] = React.useState("");
  const [detail, setDetail] = React.useState(null);
  const [templates, setTemplates] = React.useState([]);
  const [templateName, setTemplateName] = React.useState("");
  const [composer, setComposer] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [listLoading, setListLoading] = React.useState(false);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [draftBusy, setDraftBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const messagesEndRef = React.useRef(null);

  const loadOverview = React.useCallback(async () => {
    if (!projectId) return null;
    setLoading(true);
    try {
      const response = await customersApi.crmOmnichannel(projectId);
      const next = response?.overview || null;
      setOverview(next);
      return next;
    } catch (err) {
      setError(err?.message || "No se pudo leer el centro de conversaciones.");
      return null;
    } finally { setLoading(false); }
  }, [projectId]);

  const loadConversations = React.useCallback(async ({ keepSelection = true } = {}) => {
    if (!projectId) return [];
    setListLoading(true);
    try {
      const response = await customersApi.crmConversations(projectId, { page: 1, pageSize: 40 });
      const rows = response?.conversations || [];
      setConversations(rows);
      if (!keepSelection || !selectedId || !rows.some((row) => row.id === selectedId)) setSelectedId(rows[0]?.id || "");
      return rows;
    } catch (err) {
      setError(err?.message || "No se pudieron leer las conversaciones.");
      return [];
    } finally { setListLoading(false); }
  }, [projectId, selectedId]);

  const loadDetail = React.useCallback(async (conversationId, { markRead = true } = {}) => {
    if (!projectId || !conversationId) { setDetail(null); return; }
    setDetailLoading(true);
    try {
      const response = await customersApi.crmConversation(projectId, conversationId, { limit: 80 });
      setDetail({ conversation: response?.conversation || null, messages: response?.messages || [] });
      if (markRead && response?.conversation?.unreadCount > 0 && canManage) {
        customersApi.updateCrmConversation(projectId, conversationId, { markRead: true }).catch(() => {});
        setConversations((current) => current.map((row) => row.id === conversationId ? { ...row, unreadCount: 0 } : row));
      }
    } catch (err) {
      setError(err?.message || "No se pudo abrir la conversación.");
    } finally { setDetailLoading(false); }
  }, [canManage, projectId]);

  React.useEffect(() => { loadOverview(); }, [loadOverview]);
  React.useEffect(() => { if (expanded) loadConversations(); }, [expanded, loadConversations]);
  React.useEffect(() => { if (expanded && selectedId) loadDetail(selectedId); }, [expanded, selectedId, loadDetail]);

  React.useEffect(() => {
    if (!expanded) return undefined;
    const timer = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      loadOverview();
      loadConversations();
      if (selectedId) loadDetail(selectedId, { markRead: false });
    }, 15000);
    return () => window.clearInterval(timer);
  }, [expanded, loadConversations, loadDetail, loadOverview, selectedId]);

  React.useEffect(() => {
    if (!expanded || !detail?.messages?.length) return;
    messagesEndRef.current?.scrollIntoView?.({ block: "end" });
  }, [detail?.messages?.length, expanded, selectedId]);

  const whatsapp = overview?.integrations?.whatsapp || {};
  const ai = overview?.integrations?.ai || {};
  const summary = overview?.summary || {};
  const conversation = detail?.conversation || null;
  const serviceOpen = Boolean(conversation?.serviceWindowOpen);

  React.useEffect(() => {
    if (!expanded || !conversation || serviceOpen || !whatsapp.connected) return;
    if (templates.length) return;
    customersApi.crmWhatsappTemplates(projectId)
      .then((response) => setTemplates((response?.templates || []).filter((template) => !templateHasVariables(template))))
      .catch(() => {});
  }, [conversation, expanded, projectId, serviceOpen, templates.length, whatsapp.connected]);

  const refreshAll = async () => {
    setError("");
    await Promise.all([loadOverview(), expanded ? loadConversations() : Promise.resolve()]);
    if (expanded && selectedId) await loadDetail(selectedId, { markRead: false });
  };

  const send = async () => {
    if (!canManage || !conversation?.id) return;
    setBusy(true); setError("");
    try {
      let payload;
      if (serviceOpen) {
        if (!composer.trim()) return;
        payload = { text: composer.trim() };
      } else {
        const selectedTemplate = templates.find((row) => row.name === templateName);
        if (!selectedTemplate) { setError("La ventana de 24 h está cerrada: elegí una plantilla aprobada sin variables."); return; }
        payload = { template: { name: selectedTemplate.name, language: selectedTemplate.language, category: selectedTemplate.category } };
      }
      await customersApi.sendCrmConversationMessage(projectId, conversation.id, payload);
      setComposer("");
      await Promise.all([loadDetail(conversation.id, { markRead: false }), loadConversations(), loadOverview()]);
    } catch (err) {
      setError(err?.message || "No se pudo enviar el mensaje.");
    } finally { setBusy(false); }
  };

  const draft = async () => {
    if (!conversation?.id || !ai.connected) return;
    setDraftBusy(true); setError("");
    try {
      const response = await customersApi.draftCrmConversationReply(projectId, conversation.id);
      setComposer(response?.draft || "");
    } catch (err) {
      setError(err?.message || "La IA no pudo preparar una respuesta.");
    } finally { setDraftBusy(false); }
  };

  const takeConversation = async () => {
    if (!conversation?.id || !canManage) return;
    setBusy(true); setError("");
    try {
      const response = await customersApi.updateCrmConversation(projectId, conversation.id, {
        aiPaused: true,
        handoffRequired: false,
        assignToMe: true,
        status: "open",
      });
      setDetail((current) => current ? { ...current, conversation: response?.conversation || current.conversation } : current);
      await loadConversations();
    } catch (err) { setError(err?.message || "No se pudo tomar la conversación."); }
    finally { setBusy(false); }
  };

  const resumeAi = async () => {
    if (!conversation?.id || !canManage) return;
    setBusy(true); setError("");
    try {
      const response = await customersApi.updateCrmConversation(projectId, conversation.id, { aiPaused: false, handoffRequired: false, unassign: true });
      setDetail((current) => current ? { ...current, conversation: response?.conversation || current.conversation } : current);
      await loadConversations();
    } catch (err) { setError(err?.message || "No se pudo reactivar la IA."); }
    finally { setBusy(false); }
  };

  if (loading && !overview) {
    return <Paper variant="outlined" sx={{ p: 1.2 }}><Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} /><Typography variant="body2">Cargando CRM conversacional…</Typography></Stack></Paper>;
  }

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden", borderColor: "rgba(37,211,102,.24)", boxShadow: "0 8px 28px rgba(25,36,47,.035)" }}>
      <Box sx={{ px: 1.35, py: 1.15, bgcolor: "color-mix(in srgb, var(--wp-panel-surface, #fff) 96%, #25D366 4%)" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.1} alignItems={{ md: "center" }} justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Box sx={{ width: 42, height: 42, borderRadius: 2.2, display: "grid", placeItems: "center", bgcolor: "rgba(37,211,102,.14)", color: "#168A46", flex: "0 0 auto" }}><WhatsAppIcon /></Box>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={.55} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontWeight: 1000, color: INK }}>CRM conversacional · WhatsApp + IA</Typography>
                <Chip size="small" label={statusLabel(whatsapp)} sx={{ height: 21, fontWeight: 900, ...(whatsapp.connected ? { bgcolor: "rgba(37,211,102,.13)", color: "#168A46" } : {}) }} />
                <Chip size="small" icon={<AutoAwesomeRoundedIcon />} label={`IA ${statusLabel(ai).toLowerCase()}`} variant="outlined" sx={{ height: 21, fontWeight: 850 }} />
              </Stack>
              <Typography variant="caption" sx={{ color: MUTED }}>Bandeja compartida, historial por cliente, respuesta humana, copiloto IA, automatización controlada y handoff.</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap alignItems="center">
            <Chip size="small" icon={<MarkChatUnreadRoundedIcon />} label={`${summary.unreadMessages || 0} sin leer`} variant="outlined" sx={{ fontWeight: 850 }} />
            <Chip size="small" icon={<ForumRoundedIcon />} label={`${summary.open || 0} abiertas`} variant="outlined" sx={{ fontWeight: 850 }} />
            {summary.handoff ? <Chip size="small" icon={<PersonRoundedIcon />} label={`${summary.handoff} requieren humano`} color="warning" sx={{ fontWeight: 900 }} /> : null}
            <Tooltip title="Actualizar"><IconButton size="small" onClick={refreshAll}><RefreshRoundedIcon fontSize="small" /></IconButton></Tooltip>
            <Button size="small" variant={expanded ? "outlined" : "contained"} onClick={() => setExpanded((value) => !value)} sx={{ fontWeight: 950 }}>{expanded ? "Cerrar bandeja" : "Abrir bandeja"}</Button>
            <Button size="small" variant="text" onClick={onConfigure} sx={{ fontWeight: 900 }}>Configurar</Button>
          </Stack>
        </Stack>
      </Box>

      {error ? <Alert severity="error" onClose={() => setError("")} sx={{ borderRadius: 0 }}>{error}</Alert> : null}

      {!whatsapp.connected ? (
        <Box sx={{ px: 1.4, py: 1.2 }}>
          <Alert severity={whatsapp.configured ? "warning" : "info"}
            action={canManage ? <Button color="inherit" size="small" onClick={onConfigure}>{whatsapp.configured ? "Validar y activar" : "Configurar"}</Button> : null}
          >
            {whatsapp.configured
              ? "Las credenciales están guardadas, pero el canal todavía no está validado. Activarlo prueba Meta antes de habilitar envíos."
              : "Conectá Meta Cloud API para que los WhatsApp entren a WebProfit y queden unidos a la ficha y al pipeline del cliente."}
          </Alert>
        </Box>
      ) : null}

      {expanded ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "310px minmax(0,1fr)" }, minHeight: 520, borderTop: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.07)" }}>
          <Box sx={{ borderRight: { md: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.07)" }, maxHeight: 620, overflowY: "auto", bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.012)" }}>
            <Box sx={{ px: 1.1, py: .75, position: "sticky", top: 0, zIndex: 2, bgcolor: "var(--wp-panel-surface, #fff)", borderBottom: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.06)" }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between"><Typography variant="body2" sx={{ fontWeight: 950 }}>Conversaciones</Typography>{listLoading ? <CircularProgress size={15} /> : null}</Stack>
            </Box>
            {!conversations.length && !listLoading ? <Box sx={{ p: 2 }}><Typography variant="body2" sx={{ color: MUTED }}>Todavía no hay conversaciones. Cuando llegue un WhatsApp por el webhook aparecerá acá y se vinculará al cliente.</Typography></Box> : null}
            {conversations.map((row) => <ConversationRow key={row.id} row={row} selected={row.id === selectedId} onClick={() => setSelectedId(row.id)} />)}
          </Box>

          <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", height: 620 }}>
            {!selectedId ? (
              <Box sx={{ m: "auto", textAlign: "center", p: 3 }}><ForumRoundedIcon sx={{ fontSize: 42, color: "rgba(var(--wp-panel-ink-rgb,25,36,47),.25)" }} /><Typography sx={{ mt: .6, fontWeight: 900, color: INK }}>Elegí una conversación</Typography></Box>
            ) : detailLoading && !detail ? (
              <Box sx={{ m: "auto" }}><CircularProgress size={24} /></Box>
            ) : conversation ? (
              <>
                <Box sx={{ px: 1.2, py: .8, borderBottom: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.07)", bgcolor: "var(--wp-panel-surface, #fff)" }}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={.7} alignItems={{ sm: "center" }} justifyContent="space-between">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 1000, color: INK }}>{conversation.contactName || conversation.customer?.name || conversation.contactPhone}</Typography>
                      <Stack direction="row" spacing={.5} flexWrap="wrap" useFlexGap alignItems="center">
                        <Typography variant="caption" sx={{ color: MUTED }}>{conversation.contactPhone || conversation.customer?.phone}</Typography>
                        <Chip size="small" label={serviceOpen ? `Ventana abierta · ${relativeWindow(conversation.serviceWindowExpiresAt)}` : "Requiere plantilla"} color={serviceOpen ? "success" : "warning"} variant="outlined" sx={{ height: 19, fontSize: 9.5, fontWeight: 850 }} />
                        {conversation.customer?.crm?.stageKey ? <Chip size="small" label={conversation.customer.crm.stageKey} variant="outlined" sx={{ height: 19, fontSize: 9.5 }} /> : null}
                        {conversation.ai?.handoffRequired ? <Chip size="small" label="IA pidió intervención" color="warning" sx={{ height: 19, fontSize: 9.5, fontWeight: 900 }} /> : null}
                      </Stack>
                    </Box>
                    {canManage ? (
                      <Stack direction="row" spacing={.5}>
                        {conversation.ai?.paused ? (
                          <Button size="small" variant="outlined" onClick={resumeAi} disabled={busy || !ai.connected} startIcon={<AutoAwesomeRoundedIcon />} sx={{ fontWeight: 900 }}>Devolver a IA</Button>
                        ) : (
                          <Button size="small" variant="outlined" onClick={takeConversation} disabled={busy} startIcon={<PersonRoundedIcon />} sx={{ fontWeight: 900 }}>{conversation.ai?.handoffRequired ? "Tomar conversación" : "Pausar IA"}</Button>
                        )}
                      </Stack>
                    ) : null}
                  </Stack>
                </Box>

                <Box sx={{ flex: 1, overflowY: "auto", py: 1, display: "flex", flexDirection: "column", gap: .65, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.018)" }}>
                  {(detail?.messages || []).map((message) => <MessageBubble key={message.id || message.providerMessageId} message={message} />)}
                  {!detail?.messages?.length ? <Typography variant="body2" sx={{ m: "auto", color: MUTED }}>No hay mensajes almacenados todavía.</Typography> : null}
                  <Box ref={messagesEndRef} sx={{ height: 1, flex: "0 0 auto" }} />
                </Box>

                <Divider />
                <Box sx={{ p: 1, bgcolor: "var(--wp-panel-surface, #fff)" }}>
                  {!serviceOpen ? (
                    <Stack spacing={.65}>
                      <Alert severity="warning" sx={{ py: 0 }}>Pasaron más de 24 h desde el último mensaje del cliente. WhatsApp sólo permite reabrir con una plantilla aprobada.</Alert>
                      <FormControl size="small" fullWidth>
                        <Select displayEmpty value={templateName} onChange={(event) => setTemplateName(event.target.value)}>
                          <MenuItem value=""><em>Elegir plantilla aprobada sin variables</em></MenuItem>
                          {templates.map((template) => <MenuItem key={`${template.name}-${template.language}`} value={template.name}>{template.name} · {template.language} · {template.category}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <Stack direction="row" justifyContent="flex-end"><Button variant="contained" size="small" startIcon={busy ? <CircularProgress size={15} /> : <SendRoundedIcon />} disabled={!canManage || busy || !templateName || !whatsapp.connected} onClick={send} sx={{ fontWeight: 950 }}>Enviar plantilla</Button></Stack>
                    </Stack>
                  ) : (
                    <Stack spacing={.65}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={5}
                        value={composer}
                        onChange={(event) => setComposer(event.target.value)}
                        placeholder="Escribí una respuesta…"
                        disabled={!canManage || busy}
                      />
                      <Stack direction="row" spacing={.55} justifyContent="space-between" alignItems="center">
                        <Button size="small" variant="text" startIcon={draftBusy ? <CircularProgress size={14} /> : <AutoAwesomeRoundedIcon />} onClick={draft} disabled={!canManage || !ai.connected || draftBusy || busy} sx={{ fontWeight: 900 }}>Redactar con IA</Button>
                        <Button size="small" variant="contained" startIcon={busy ? <CircularProgress size={15} /> : <SendRoundedIcon />} onClick={send} disabled={!canManage || busy || !composer.trim() || !whatsapp.connected} sx={{ fontWeight: 950 }}>Enviar</Button>
                      </Stack>
                    </Stack>
                  )}
                </Box>
              </>
            ) : null}
          </Box>
        </Box>
      ) : null}
    </Paper>
  );
}
